import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import MaritacaLogoBranca from '../static/images/maritaca_logo_branca.png';
import bgLeque from '../static/images/maritaca-bg-leque.webp';
import bgCirculos from '../static/images/maritaca-bg-circulos.webp';
import { API_BASE } from '../shared/Constants';

// Auth0 email-verification denials vary by tenant and wording (dev says
// "Confirme seu e-mail...", prod says "Por favor, confirme o email...", plus
// English variants). Match the semantic shape, not an exact phrase, so the
// resend-verification recovery button always appears for blocked users.
const matchesEmailVerificationWarning = (text?: string | null) => {
  if (!text) return false;
  const normalized = text.toLowerCase().replace(/-/g, ''); // "e-mail" -> "email"
  return /confirm|verif/.test(normalized) && /e?mails?/.test(normalized);
};

const COLORS = {
  primary: '#1a3c34',
  primaryHover: '#143028',
  accent: '#e96b3a',
  accentSoft: '#f49773',
  bg: '#fafbfc',
  surface: '#f4f5f7',
  card: '#ffffff',
  border: '#e6e8eb',
  borderStrong: '#d4d7dc',
  text: '#1a1a1a',
  muted: '#6b7280',
  mutedSoft: '#9ca3af',
};

type Feature = {
  icon: React.ReactNode;
  title: string;
  desc: string;
};

type UseCase = {
  emoji: string;
  title: string;
  desc: string;
  bullets: string[];
};

const FEATURES: Feature[] = [
  {
    icon: '🌐',
    title: 'Busca na web em tempo real',
    desc: 'Pergunte sobre fatos recentes e receba respostas com fontes citadas.',
  },
  {
    icon: '🔎',
    title: 'Deep Research',
    desc: 'Pesquisas profundas que combinam várias fontes em um relatório estruturado.',
  },
  {
    icon: '📎',
    title: 'Arquivos e planilhas',
    desc: 'Envie PDFs, planilhas e documentos. O Sabiá lê, resume e responde.',
  },
  {
    icon: '🇧🇷',
    title: 'Modelos treinados para o Brasil',
    desc: 'O Sabiá entende contexto, gírias e nuances do português brasileiro.',
  },
];

const USE_CASES: UseCase[] = [
  {
    emoji: '📚',
    title: 'Estudo e pesquisa',
    desc: 'Explique conceitos, resolva exercícios e construa resumos prontos pra revisão.',
    bullets: ['Resumos de matéria', 'Exercícios resolvidos', 'Explicações passo a passo'],
  },
  {
    emoji: '📄',
    title: 'Resumir documentos',
    desc: 'Envie contratos, processos jurídicos, artigos ou relatórios e extraia o que importa.',
    bullets: ['Análise de contratos', 'Resumos jurídicos', 'Pontos-chave de relatórios'],
  },
  {
    emoji: '✍️',
    title: 'Redação e e-mails',
    desc: 'Escreva, revise e ajuste tom de e-mails, redações e textos longos em português nativo.',
    bullets: ['Tom formal ou informal', 'Revisão gramatical', 'Reescrita com novo estilo'],
  },
  {
    emoji: '💻',
    title: 'Programação',
    desc: 'Tire dúvidas, peça refatorações e execute código Python direto no chat.',
    bullets: ['Execução de Python', 'Debug de erros', 'Refatorações guiadas'],
  },
  {
    emoji: '📊',
    title: 'Análise de dados',
    desc: 'Envie planilhas e CSVs. O Sabiá faz cálculos, gera gráficos e interpreta números.',
    bullets: ['Estatísticas descritivas', 'Gráficos automáticos', 'Cruzamento de colunas'],
  },
  {
    emoji: '🎯',
    title: 'Trabalho do dia a dia',
    desc: 'Brainstorm, atas de reunião, planejamento — qualquer tarefa textual fica mais rápida.',
    bullets: ['Atas e checklists', 'Brainstorm guiado', 'Estruturação de ideias'],
  },
];

function Login() {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [activeUseCase, setActiveUseCase] = useState(0);
  const isEmailVerificationAlert = matchesEmailVerificationWarning(alertMessage);

  const navigate = useNavigate();

  const urlError = new URLSearchParams(window.location.search).get('error');
  const urlErrorDescription = new URLSearchParams(window.location.search).get('error_description');
  const hasAuthErrorParams = Boolean(urlError || urlErrorDescription);

  useEffect(() => {
    if (urlError) {
      if (urlErrorDescription === 'account_linked') {
        const url = new URL(window.location.href);
        url.searchParams.delete('error');
        url.searchParams.delete('error_description');
        window.history.replaceState({}, document.title, url.toString());
        loginWithRedirect({
          authorizationParams: {
            scope: 'openid profile email offline_access',
            redirect_uri: window.location.origin + '/auth',
          },
          appState: { returnTo: '/' },
        });
        return;
      }

      setAlertMessage(urlErrorDescription || 'Erro de autenticação');
      if (matchesEmailVerificationWarning(urlErrorDescription)) {
        setShowResendButton(true);
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      url.searchParams.delete('error_description');
      window.history.replaceState({}, document.title, url.toString());
      setLoading(false);
      setIsSignupMode(false);
      setAuthOpen(true);
    }
  }, [urlError, urlErrorDescription, loginWithRedirect]);

  useEffect(() => {
    if (hasAuthErrorParams || isEmailVerificationAlert) return;
    if (!isLoading && isAuthenticated) navigate('/', { replace: true });
  }, [isLoading, isAuthenticated, navigate, isEmailVerificationAlert, hasAuthErrorParams]);

  const handleLogin = async () => {
    setLoading(true);
    setAlertMessage(null);
    setIsSignupMode(false);
    try {
      await loginWithRedirect({
        authorizationParams: {
          connection: 'Username-Password-Authentication',
          scope: 'openid profile email offline_access',
          screen_hint: 'login',
          login_hint: email || undefined,
          redirect_uri: window.location.origin + '/auth',
          prompt: 'login',
        },
        appState: { returnTo: '/', mode: 'login' },
      });
    } catch (err) {
      console.error('Erro ao iniciar login:', err);
      setAlertMessage('Erro ao fazer login. Tente novamente.');
      setLoading(false);
    }
  };

  const handleSocialLogin = async (connection: string) => {
    setAlertMessage(null);
    try {
      await loginWithRedirect({
        authorizationParams: {
          connection,
          scope: 'openid profile email offline_access',
          redirect_uri: window.location.origin + '/auth',
          prompt: 'login',
        },
        appState: { returnTo: '/' },
      });
    } catch (err) {
      console.error('Erro no login social:', err);
      setAlertMessage('Erro ao fazer login social');
    }
  };

  const goToSignupDirect = () => {
    setLoading(true);
    setAlertMessage(null);
    setIsSignupMode(true);
    const domain = process.env.REACT_APP_AUTH_DOMAIN;
    const clientId = process.env.REACT_APP_AUTH_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin);
    const scope = encodeURIComponent('openid profile email offline_access');
    const signupUrl =
      `https://${domain}/authorize?` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `redirect_uri=${redirectUri}&` +
      `scope=${scope}&` +
      `screen_hint=signup&` +
      `prompt=login&` +
      `initial_screen=signUp` +
      (email ? `&login_hint=${encodeURIComponent(email)}` : '');
    window.location.href = signupUrl;
  };

  const handleResendVerification = async () => {
    if (!email) {
      setAlertMessage('Por favor, insira seu email primeiro.');
      return;
    }
    setResendLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        setAlertMessage('Email de verificação reenviado com sucesso! Verifique sua caixa de entrada.');
        setShowResendButton(false);
      } else {
        const errorData = await response.json();
        setAlertMessage(errorData.detail || 'Erro ao reenviar email de verificação.');
      }
    } catch {
      setAlertMessage('Erro ao reenviar email de verificação. Tente novamente.');
    } finally {
      setResendLoading(false);
    }
  };

  const closeAuth = () => {
    if (loading) return;
    setAuthOpen(false);
    setAlertMessage(null);
    setShowResendButton(false);
  };

  return (
    <PageRoot>
      <Header>
        <HeaderInner>
          <Brand>
            <BrandLogo src={MaritacaLogoBranca} alt="Maritaca" />
            <BrandName>maritaca.ai</BrandName>
          </Brand>
          <HeaderActions>
            <HeaderLink href="https://docs.maritaca.ai" target="_blank" rel="noopener noreferrer">
              Documentação
            </HeaderLink>
            <HeaderLink href="https://www.maritaca.ai/about" target="_blank" rel="noopener noreferrer">
              Sobre
            </HeaderLink>
            <EntrarButton onClick={() => setAuthOpen(true)}>Entrar</EntrarButton>
          </HeaderActions>
        </HeaderInner>
      </Header>

      <Hero>
        <HeroBg src={bgLeque} aria-hidden="true" decoding="async" />
        <HeroInner>
          <HeroPill>
            <PillDot /> Sabiá-4 já disponível
          </HeroPill>
          <HeroTitle>
            Converse com a IA
            <br />
            <HeroTitleAccent>que entende o Brasil</HeroTitleAccent>
          </HeroTitle>
          <HeroSubtitle>
            O chat da Maritaca AI fala português de verdade, busca na web, lê seus documentos e
            executa código.
          </HeroSubtitle>
          <HeroCtaRow>
            <PrimaryCta onClick={() => setAuthOpen(true)}>Comece grátis →</PrimaryCta>
            <SecondaryCta
              href="https://www.maritaca.ai"
              target="_blank"
              rel="noopener noreferrer"
            >
              Saiba mais
            </SecondaryCta>
          </HeroCtaRow>
          <HeroNote>
            <NoteStrong>Conta grátis</NoteStrong> com acesso aos modelos Sabiá. Sem cartão de
            crédito.
          </HeroNote>

          <ChatPreview>
            <ChatHeader>
              <ChatDots>
                <Dot $color="#ff5f57" />
                <Dot $color="#febc2e" />
                <Dot $color="#28c840" />
              </ChatDots>
              <ChatTitle>chat.maritaca.ai</ChatTitle>
              <span />
            </ChatHeader>
            <ChatBody>
              <UserBubble>
                Resuma os principais pontos do PL 2.630 e me diga o status atual no Congresso.
              </UserBubble>
              <AssistantBubble>
                <AsstHeader>
                  <AsstAvatar>S</AsstAvatar>
                  <AsstName>Sabiá-4</AsstName>
                  <ToolChip>🌐 Busca na web</ToolChip>
                </AsstHeader>
                <AsstText>
                  Aqui está um resumo dos principais pontos do <b>PL 2.630/2020</b>, conhecido como
                  "PL das Fake News", e seu status atual no Congresso:
                </AsstText>
                <AsstSubhead>Principais pontos do PL 2.630:</AsstSubhead>
                <AsstList>
                  <li>
                    Institui a Lei Brasileira de Liberdade, Responsabilidade e Transparência na
                    Internet <Cite>[1]</Cite>.
                  </li>
                  <li>
                    Estabelece regras de moderação de conteúdo, transparência de algoritmos e
                    combate à desinformação <Cite>[2]</Cite>.
                  </li>
                  <li>
                    Obriga plataformas a informarem critérios usados para sugerir, priorizar ou
                    remover conteúdos.
                  </li>
                </AsstList>
                <AsstRefHead>Referências</AsstRefHead>
                <AsstRefList>
                  <li>
                    <RefNum>[1]</RefNum>
                    <RefBody>
                      Senado Federal —{' '}
                      <RefDomain>senado.leg.br</RefDomain>
                    </RefBody>
                  </li>
                  <li>
                    <RefNum>[2]</RefNum>
                    <RefBody>
                      Politize —{' '}
                      <RefDomain>politize.com.br</RefDomain>
                    </RefBody>
                  </li>
                </AsstRefList>
              </AssistantBubble>
            </ChatBody>
          </ChatPreview>
        </HeroInner>
      </Hero>

      <FeaturesSection>
        <SectionInner>
          <SectionEyebrow>Recursos do chat</SectionEyebrow>
          <SectionTitle>Tudo o que você precisa pra conversar com IA</SectionTitle>
          <FeaturesGrid>
            {FEATURES.map((f) => (
              <FeatureCard key={f.title}>
                <FeatureIcon>{f.icon}</FeatureIcon>
                <FeatureTitle>{f.title}</FeatureTitle>
                <FeatureDesc>{f.desc}</FeatureDesc>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </SectionInner>
      </FeaturesSection>

      <BrasilSection>
        <SectionInner>
          <SectionEyebrow>Estudo e leis</SectionEyebrow>
          <SectionTitle>Estuda com você. Entende as leis do Brasil.</SectionTitle>
          <SectionLead>
            O Sabiá foi treinado com provas e legislação brasileiras de verdade. Ajuda você a
            estudar pro ENEM, OAB e concursos — e lê contratos, processos e leis em linguagem
            acessível.
          </SectionLead>
          <BrasilGrid>
            <BrasilCard>
              <BrasilEmoji>📚</BrasilEmoji>
              <BrasilTitle>ENEM e vestibulares</BrasilTitle>
              <BrasilDesc>
                Resolve questões, explica matéria e te ajuda a montar redação nota mil.
              </BrasilDesc>
              <BrasilChips>
                <BrasilChip>Acerta 88% das questões do ENEM</BrasilChip>
              </BrasilChips>
            </BrasilCard>
            <BrasilCard>
              <BrasilEmoji>⚖️</BrasilEmoji>
              <BrasilTitle>OAB e concursos</BrasilTitle>
              <BrasilDesc>
                Estuda com você pra OAB, CPNU e outros concursos brasileiros — da teoria à
                resolução de questões.
              </BrasilDesc>
              <BrasilChips>
                <BrasilChip>87% na 1ª fase da OAB</BrasilChip>
                <BrasilChip>91% no CPNU</BrasilChip>
              </BrasilChips>
            </BrasilCard>
            <BrasilCard>
              <BrasilEmoji>📜</BrasilEmoji>
              <BrasilTitle>Leis e documentos</BrasilTitle>
              <BrasilDesc>
                Lê contratos, decisões e legislação brasileira. Resumo de processo em segundos,
                em linguagem que dá pra entender.
              </BrasilDesc>
            </BrasilCard>
          </BrasilGrid>
        </SectionInner>
      </BrasilSection>

      <UseCasesSection>
        <SectionInner>
          <SectionEyebrow>Casos de uso</SectionEyebrow>
          <SectionTitle>O que dá pra fazer no chat</SectionTitle>
          <SectionLead>
            Da redação ao código, passando por análise de documentos — escolha um caso e veja
            como o Sabiá ajuda.
          </SectionLead>
          <UseCasesGrid>
            <UseCaseTabs>
              {USE_CASES.map((uc, i) => (
                <UseCaseTab
                  key={uc.title}
                  type="button"
                  $active={activeUseCase === i}
                  onClick={() => setActiveUseCase(i)}
                >
                  <UseCaseTabEmoji>{uc.emoji}</UseCaseTabEmoji>
                  <UseCaseTabLabel>{uc.title}</UseCaseTabLabel>
                </UseCaseTab>
              ))}
            </UseCaseTabs>
            <UseCasePanel>
              <UseCasePanelHead>
                <UseCasePanelIcon>{USE_CASES[activeUseCase].emoji}</UseCasePanelIcon>
                <UseCasePanelTitle>{USE_CASES[activeUseCase].title}</UseCasePanelTitle>
              </UseCasePanelHead>
              <UseCasePanelDesc>{USE_CASES[activeUseCase].desc}</UseCasePanelDesc>
              <UseCasePanelList>
                {USE_CASES[activeUseCase].bullets.map((b) => (
                  <li key={b}>
                    <BulletCheck>✓</BulletCheck>
                    {b}
                  </li>
                ))}
              </UseCasePanelList>
              <UseCasePanelCta onClick={() => setAuthOpen(true)}>
                Experimentar agora →
              </UseCasePanelCta>
            </UseCasePanel>
          </UseCasesGrid>
        </SectionInner>
      </UseCasesSection>

      <CtaSection>
        <CtaBg src={bgCirculos} aria-hidden="true" decoding="async" />
        <CtaInner>
          <CtaTitle>Comece a conversar com o Sabiá</CtaTitle>
          <CtaSub>
            Crie sua conta grátis e tenha acesso aos modelos brasileiros mais avançados pra
            português.
          </CtaSub>
          <PrimaryCta onClick={() => setAuthOpen(true)}>Criar conta grátis →</PrimaryCta>
        </CtaInner>
      </CtaSection>

      <Footer>
        <FooterInner>
          <FooterCol>
            <FooterLogo>
              <BrandLogo src={MaritacaLogoBranca} alt="Maritaca" />
              <BrandName>maritaca.ai</BrandName>
            </FooterLogo>
            <FooterTag>IA brasileira pra todo mundo conversar.</FooterTag>
          </FooterCol>
          <FooterCol>
            <FooterHead>Produto</FooterHead>
            <FooterLink href="https://www.maritaca.ai" target="_blank" rel="noopener noreferrer">
              Site
            </FooterLink>
            <FooterLink href="https://plataforma.maritaca.ai" target="_blank" rel="noopener noreferrer">
              Plataforma de API
            </FooterLink>
            <FooterLink href="https://docs.maritaca.ai" target="_blank" rel="noopener noreferrer">
              Documentação
            </FooterLink>
          </FooterCol>
          <FooterCol>
            <FooterHead>Legal</FooterHead>
            <FooterLink
              href="https://www.maritaca.ai/termos-e-condicoes"
              target="_blank"
              rel="noopener noreferrer"
            >
              Termos de uso
            </FooterLink>
            <FooterLink
              href="https://www.maritaca.ai/politica-de-privacidade"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacidade
            </FooterLink>
          </FooterCol>
        </FooterInner>
        <FooterBottom>© {new Date().getFullYear()} Maritaca AI</FooterBottom>
      </Footer>

      {authOpen && (
        <DialogOverlay onClick={closeAuth}>
          <Dialog
            role="dialog"
            aria-modal="true"
            aria-label="Entrar na Maritaca"
            onClick={(e) => e.stopPropagation()}
          >
            <DialogClose type="button" onClick={closeAuth} aria-label="Fechar">
              ×
            </DialogClose>
            <DialogHeader>
              <DialogTitle>Entrar na Maritaca</DialogTitle>
              <DialogSub>Acesse o chat com IA brasileira em segundos.</DialogSub>
            </DialogHeader>

            {alertMessage && (
              <Alert>
                <AlertRow>
                  <span>⚠️</span>
                  <AlertText>{alertMessage}</AlertText>
                </AlertRow>
                {showResendButton && (
                  <ResendButton
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                  >
                    {resendLoading ? 'Reenviando...' : 'Reenviar email de verificação'}
                  </ResendButton>
                )}
              </Alert>
            )}

            <GoogleButton type="button" onClick={() => handleSocialLogin('google-oauth2')}>
              <GoogleSvg viewBox="0 0 18 18" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"
                />
                <path
                  fill="#34A853"
                  d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-2.7.75 4.8 4.8 0 0 1-4.52-3.36H1.83v2.07A8 8 0 0 0 8.98 17z"
                />
                <path
                  fill="#FBBC05"
                  d="M4.46 10.41a4.8 4.8 0 0 1-.25-1.41c0-.49.9-.97.25-1.41V5.52H1.83a8 8 0 0 0 0 7.16l2.63-2.27z"
                />
                <path
                  fill="#EA4335"
                  d="M8.98 3.58c1.32 0 2.5.45 3.44 1.35l2.54-2.54a8 8 0 0 0-5.98-2.39 8 8 0 0 0-7.15 4.42l2.63 2.27c.63-1.89 2.39-3.11 4.52-3.11z"
                />
              </GoogleSvg>
              Continuar com Google
            </GoogleButton>

            <Divider>
              <DividerLine />
              <DividerText>ou</DividerText>
              <DividerLine />
            </Divider>

            <LoginForm
              onSubmit={(e) => {
                e.preventDefault();
                handleLogin();
              }}
            >
              <FormField>
                <FormLabel>Email</FormLabel>
                <FormInput
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  autoFocus
                />
              </FormField>
              <SubmitButton type="submit" disabled={loading}>
                {loading && !isSignupMode ? 'Entrando...' : 'Entrar'}
              </SubmitButton>
              <SignupSwitch type="button" onClick={goToSignupDirect} disabled={loading}>
                {loading && isSignupMode ? 'Criando conta...' : 'Não tem conta? Criar uma'}
              </SignupSwitch>
            </LoginForm>

            <DialogTerms>
              Ao continuar, você aceita os{' '}
              <a
                href="https://www.maritaca.ai/termos-e-condicoes"
                target="_blank"
                rel="noopener noreferrer"
              >
                Termos de Uso
              </a>{' '}
              e a{' '}
              <a
                href="https://www.maritaca.ai/politica-de-privacidade"
                target="_blank"
                rel="noopener noreferrer"
              >
                Política de Privacidade
              </a>
              .
            </DialogTerms>
          </Dialog>
        </DialogOverlay>
      )}
    </PageRoot>
  );
}

export default Login;

/* ============================== Styled ============================== */

const PageRoot = styled.div`
  min-height: 100vh;
  background: ${COLORS.bg};
  color: ${COLORS.text};
  font-family: 'Inter', 'Lexend', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow-x: hidden;
`;

/* ---------- Header ---------- */
const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 30;
  background: ${COLORS.primary};
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const HeaderInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  height: 72px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BrandLogo = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;

const BrandName = styled.span`
  font-size: 21px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #fff;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const HeaderLink = styled.a`
  display: none;
  padding: 8px 14px;
  font-size: 13.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  border-radius: 6px;
  transition: color 0.15s;
  &:hover {
    color: #fff;
  }
  @media (min-width: 640px) {
    display: inline-flex;
  }
`;

const EntrarButton = styled.button`
  margin-left: 8px;
  height: 38px;
  padding: 0 20px;
  background: #fff;
  color: ${COLORS.primary};
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: rgba(255, 255, 255, 0.92);
  }
`;

/* ---------- Hero ---------- */
const Hero = styled.section`
  position: relative;
  overflow: hidden;
  isolation: isolate;
`;

const HeroBg = styled.img`
  position: absolute;
  left: 50%;
  top: 0;
  width: 2200px;
  max-width: none;
  transform: translate(-50%, -20%);
  opacity: 0.18;
  pointer-events: none;
  z-index: -1;
  -webkit-mask-image: radial-gradient(ellipse at center, #000 40%, transparent 75%);
          mask-image: radial-gradient(ellipse at center, #000 40%, transparent 75%);
`;

const HeroInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 24px 64px;
  text-align: center;
  @media (min-width: 768px) {
    padding: 104px 24px 80px;
  }
`;

const HeroPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: ${COLORS.card};
  border: 1px solid ${COLORS.border};
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 500;
  color: ${COLORS.muted};
`;

const PillDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${COLORS.accent};
  box-shadow: 0 0 0 3px rgba(233, 107, 58, 0.18);
`;

const HeroTitle = styled.h1`
  margin: 20px 0 0;
  font-size: 44px;
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.05;
  color: ${COLORS.text};
  @media (min-width: 768px) {
    font-size: 64px;
  }
`;

const HeroTitleAccent = styled.span`
  color: ${COLORS.primary};
`;

const HeroSubtitle = styled.p`
  margin: 22px auto 0;
  max-width: 640px;
  font-size: 16px;
  line-height: 1.55;
  color: ${COLORS.muted};
  @media (min-width: 768px) {
    font-size: 17.5px;
  }
`;

const HeroCtaRow = styled.div`
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ctaBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid transparent;
  font-family: inherit;
`;

const PrimaryCta = styled.button`
  ${ctaBase};
  background: ${COLORS.primary};
  color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:hover {
    background: ${COLORS.primaryHover};
  }
`;

const SecondaryCta = styled.a`
  ${ctaBase};
  background: ${COLORS.card};
  color: ${COLORS.text};
  border-color: ${COLORS.border};
  &:hover {
    border-color: ${COLORS.borderStrong};
    background: ${COLORS.surface};
  }
`;

const HeroNote = styled.p`
  margin-top: 16px;
  font-size: 13px;
  color: ${COLORS.muted};
`;

const NoteStrong = styled.strong`
  color: ${COLORS.text};
  font-weight: 600;
`;

/* ---------- Chat preview ---------- */
const ChatPreview = styled.div`
  margin: 64px auto 0;
  max-width: 760px;
  background: ${COLORS.card};
  border: 1px solid ${COLORS.border};
  border-radius: 14px;
  box-shadow:
    0 4px 8px rgba(60, 66, 87, 0.06),
    0 18px 40px rgba(60, 66, 87, 0.08);
  overflow: hidden;
  text-align: left;
`;

const ChatHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 10px 14px;
  background: ${COLORS.surface};
  border-bottom: 1px solid ${COLORS.border};
`;

const ChatDots = styled.div`
  display: flex;
  gap: 6px;
`;

const Dot = styled.span<{ $color: string }>`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: ${(p) => p.$color};
`;

const ChatTitle = styled.span`
  font-size: 12.5px;
  font-weight: 500;
  color: ${COLORS.mutedSoft};
`;

const ChatBody = styled.div`
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-size: 14.5px;
  line-height: 1.55;
`;

const UserBubble = styled.div`
  align-self: flex-end;
  max-width: 78%;
  padding: 11px 16px;
  background: ${COLORS.primary};
  color: #fff;
  border-radius: 16px 16px 4px 16px;
`;

const AssistantBubble = styled.div`
  align-self: flex-start;
  max-width: 92%;
  padding: 14px 16px;
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  color: ${COLORS.text};
  border-radius: 16px 16px 16px 4px;
`;

const AsstHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const AsstAvatar = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${COLORS.primary};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`;

const AsstName = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${COLORS.text};
`;

const ToolChip = styled.span`
  margin-left: 2px;
  padding: 2px 8px;
  background: rgba(233, 107, 58, 0.1);
  color: #9a3514;
  font-size: 11px;
  font-weight: 500;
  border-radius: 999px;
  border: 1px solid rgba(233, 107, 58, 0.2);
`;

const AsstText = styled.p`
  margin: 0;
  color: ${COLORS.text};
  font-size: 14px;
  & b {
    color: ${COLORS.primary};
  }
`;

const AsstList = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
  color: ${COLORS.text};
  & li {
    margin: 4px 0;
    font-size: 13.5px;
  }
`;

const AsstSubhead = styled.p`
  margin: 10px 0 4px;
  font-size: 13.5px;
  font-weight: 600;
  color: ${COLORS.text};
`;

const Cite = styled.sup`
  margin-left: 2px;
  color: ${COLORS.accent};
  font-weight: 600;
  font-size: 11px;
`;

const AsstRefHead = styled.p`
  margin: 14px 0 6px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${COLORS.mutedSoft};
`;

const AsstRefList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  & li {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 12.5px;
    color: ${COLORS.muted};
  }
`;

const RefNum = styled.span`
  color: ${COLORS.accent};
  font-weight: 600;
  font-size: 11.5px;
  flex-shrink: 0;
`;

const RefBody = styled.span`
  color: ${COLORS.muted};
`;

const RefDomain = styled.span`
  color: ${COLORS.primary};
  text-decoration: underline;
  text-decoration-color: ${COLORS.border};
`;

/* ---------- Features section ---------- */
const FeaturesSection = styled.section`
  background: ${COLORS.surface};
  border-top: 1px solid ${COLORS.border};
  border-bottom: 1px solid ${COLORS.border};
`;

const SectionInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 72px 24px;
`;

const SectionEyebrow = styled.p`
  margin: 0 0 8px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.muted};
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 30px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${COLORS.text};
  @media (min-width: 768px) {
    font-size: 34px;
  }
`;

const SectionLead = styled.p`
  max-width: 620px;
  margin: 12px 0 0;
  font-size: 15.5px;
  line-height: 1.55;
  color: ${COLORS.muted};
`;

const FeaturesGrid = styled.div`
  margin-top: 36px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FeatureCard = styled.div`
  background: ${COLORS.card};
  border: 1px solid ${COLORS.border};
  border-radius: 12px;
  padding: 24px;
`;

const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const FeatureTitle = styled.h3`
  margin: 16px 0 6px;
  font-size: 15.5px;
  font-weight: 600;
  color: ${COLORS.text};
  letter-spacing: -0.01em;
`;

const FeatureDesc = styled.p`
  margin: 0;
  font-size: 13.5px;
  line-height: 1.55;
  color: ${COLORS.muted};
`;

/* ---------- Brasil (estudo + leis) section ---------- */
const BrasilSection = styled.section`
  background: ${COLORS.bg};
`;

const BrasilGrid = styled.div`
  margin-top: 36px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const BrasilCard = styled.div`
  background: ${COLORS.card};
  border: 1px solid ${COLORS.border};
  border-radius: 14px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BrasilEmoji = styled.div`
  font-size: 28px;
  line-height: 1;
`;

const BrasilTitle = styled.h3`
  margin: 4px 0 0;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${COLORS.text};
`;

const BrasilDesc = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: ${COLORS.muted};
`;

const BrasilChips = styled.div`
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const BrasilChip = styled.span`
  padding: 4px 10px;
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  border-radius: 999px;
  font-size: 12px;
  color: ${COLORS.muted};
`;

/* ---------- Use cases section ---------- */
const UseCasesSection = styled.section`
  background: ${COLORS.bg};
  border-top: 1px solid ${COLORS.border};
`;

const UseCasesGrid = styled.div`
  margin-top: 32px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  @media (min-width: 1024px) {
    grid-template-columns: 280px 1fr;
  }
`;

const UseCaseTabs = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 4px;
  @media (min-width: 1024px) {
    flex-direction: column;
    overflow: visible;
    padding-bottom: 0;
  }
`;

const UseCaseTab = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: ${(p) => (p.$active ? COLORS.card : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? COLORS.borderStrong : 'transparent')};
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  color: ${(p) => (p.$active ? COLORS.text : COLORS.muted)};
  transition: all 0.12s;
  font-family: inherit;
  &:hover {
    color: ${COLORS.text};
    background: ${COLORS.card};
    border-color: ${COLORS.border};
  }
`;

const UseCaseTabEmoji = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const UseCaseTabLabel = styled.span`
  font-size: 13.5px;
  font-weight: 500;
`;

const UseCasePanel = styled.div`
  background: ${COLORS.card};
  border: 1px solid ${COLORS.border};
  border-radius: 14px;
  padding: 32px;
`;

const UseCasePanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const UseCasePanelIcon = styled.span`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
`;

const UseCasePanelTitle = styled.h3`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: ${COLORS.text};
`;

const UseCasePanelDesc = styled.p`
  margin: 18px 0 0;
  font-size: 15px;
  line-height: 1.6;
  color: ${COLORS.muted};
`;

const UseCasePanelList = styled.ul`
  list-style: none;
  margin: 22px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  & li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 13.5px;
    color: ${COLORS.text};
  }
`;

const BulletCheck = styled.span`
  color: #2e9c5b;
  font-weight: 700;
  flex-shrink: 0;
`;

const UseCasePanelCta = styled.button`
  margin-top: 26px;
  ${ctaBase};
  height: 42px;
  padding: 0 20px;
  font-size: 14px;
  background: ${COLORS.primary};
  color: #fff;
  &:hover {
    background: ${COLORS.primaryHover};
  }
`;

/* ---------- CTA section ---------- */
const CtaSection = styled.section`
  position: relative;
  overflow: hidden;
  background: ${COLORS.primary};
  color: #fff;
`;

const CtaBg = styled.img`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.08;
  pointer-events: none;
`;

const CtaInner = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  padding: 80px 24px;
  text-align: center;
`;

const CtaTitle = styled.h2`
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #fff;
  @media (min-width: 768px) {
    font-size: 40px;
  }
`;

const CtaSub = styled.p`
  margin: 16px auto 28px;
  max-width: 560px;
  font-size: 16px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.78);
`;

/* ---------- Footer ---------- */
const Footer = styled.footer`
  background: ${COLORS.primary};
  color: rgba(255, 255, 255, 0.65);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const FooterInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 56px 24px 32px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 36px;
  @media (min-width: 640px) {
    grid-template-columns: 2fr 1fr 1fr;
  }
`;

const FooterCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

const FooterTag = styled.p`
  margin: 0;
  font-size: 13.5px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.55);
  max-width: 320px;
`;

const FooterHead = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 4px;
`;

const FooterLink = styled.a`
  font-size: 13.5px;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: color 0.12s;
  &:hover {
    color: #fff;
  }
`;

const FooterBottom = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 18px 24px 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.45);
`;

/* ---------- Dialog ---------- */
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: ${fadeIn} 0.15s ease-out;
`;

const Dialog = styled.div`
  position: relative;
  width: 100%;
  max-width: 420px;
  background: ${COLORS.card};
  border-radius: 14px;
  padding: 30px 30px 26px;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.12),
    0 30px 60px -10px rgba(0, 0, 0, 0.2);
  animation: ${slideUp} 0.18s ease-out;
`;

const DialogClose = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 20px;
  color: ${COLORS.muted};
  cursor: pointer;
  &:hover {
    background: ${COLORS.surface};
    color: ${COLORS.text};
  }
`;

const DialogHeader = styled.div`
  text-align: center;
  margin-bottom: 22px;
`;

const DialogTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: ${COLORS.text};
`;

const DialogSub = styled.p`
  margin: 8px 0 0;
  font-size: 13.5px;
  color: ${COLORS.muted};
`;

const Alert = styled.div`
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  border-radius: 8px;
  color: #7f1d1d;
`;

const AlertRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const AlertText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
`;

const ResendButton = styled.button`
  margin-top: 10px;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid #fca5a5;
  border-radius: 999px;
  font-size: 12px;
  color: #7f1d1d;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #fef2f2;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GoogleButton = styled.button`
  width: 100%;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: ${COLORS.card};
  border: 1px solid ${COLORS.borderStrong};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.text};
  cursor: pointer;
  transition: all 0.12s;
  font-family: inherit;
  &:hover {
    background: ${COLORS.surface};
  }
`;

const GoogleSvg = styled.svg`
  width: 18px;
  height: 18px;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 18px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${COLORS.border};
`;

const DividerText = styled.span`
  font-size: 12px;
  color: ${COLORS.mutedSoft};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${COLORS.text};
`;

const FormInput = styled.input`
  height: 42px;
  padding: 0 12px;
  background: ${COLORS.card};
  border: 1px solid ${COLORS.borderStrong};
  border-radius: 8px;
  font-size: 14px;
  color: ${COLORS.text};
  font-family: inherit;
  transition: border-color 0.12s, box-shadow 0.12s;
  &::placeholder {
    color: ${COLORS.mutedSoft};
  }
  &:focus {
    outline: none;
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 3px rgba(26, 60, 52, 0.12);
  }
`;

const SubmitButton = styled.button`
  height: 44px;
  background: ${COLORS.primary};
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s;
  font-family: inherit;
  &:hover:not(:disabled) {
    background: ${COLORS.primaryHover};
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const SignupSwitch = styled.button`
  background: none;
  border: none;
  font-size: 13px;
  color: ${COLORS.primary};
  cursor: pointer;
  font-weight: 500;
  padding: 4px;
  font-family: inherit;
  &:hover:not(:disabled) {
    text-decoration: underline;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DialogTerms = styled.p`
  margin: 20px 0 0;
  text-align: center;
  font-size: 11.5px;
  line-height: 1.55;
  color: ${COLORS.mutedSoft};
  & a {
    color: ${COLORS.muted};
    text-decoration: underline;
    &:hover {
      color: ${COLORS.text};
    }
  }
`;
